import { _ as _extends } from '../_rollupPluginBabelHelpers-e8fb2e5c.js';
import React__default, { forwardRef, useRef, useState, useContext, useCallback, useMemo, Children, useEffect } from 'react';
import { _ as _default } from '../Popover-f4d1cac0.js';
import { u as useSwipeable } from '../index-122432cd.js';
import { c as classnames } from '../index-6ff23041.js';
import PropTypes from 'prop-types';
import { n as noop, s as stopEvent } from '../index-a0e4e333.js';
import useDeviceType from '../hooks/useDeviceType.js';
import useUpdatableRef from '../hooks/useUpdatableRef.js';
import { e as popoverConfig, s as screenTypes } from '../configs-fed6ac34.js';
import { GeneUIDesignSystemContext } from '../GeneUIProvider/index.js';
import Portal from '../Portal/index.js';
import CustomScrollbar from '../Scrollbar/index.js';
import { s as styleInject } from '../style-inject.es-746bb8ed.js';
import { g as guid } from '../guid-8ddf77b3.js';
import '../_commonjsHelpers-24198af3.js';
import 'react-dom';
import '../dateValidation-67caec66.js';
import '../hooks/useWindowSize.js';
import '../hooks/useForceUpdate.js';

var css_248z = "[data-gene-ui-version=\"2.13.0\"] .popover-positioner{--popover-border-radius:1rem;--popover-shadow:0 0.2rem 0.4rem 0 #0000000d,0 0 0 1px rgba(var(--background-sc-rgb),0.08);z-index:400}[data-gene-ui-version=\"2.13.0\"] .popover-positioner.cr-smooth-radius{--popover-border-radius:0.4rem}[data-gene-ui-version=\"2.13.0\"] .popover-positioner.mobile-view{--popover-border-radius:0.8rem 0.8rem 0 0;--popover-shadow:0 -1px 0.2rem 0 #0000000d;bottom:0;left:0!important;overflow:hidden;position:fixed!important;top:0!important;width:100%}[data-gene-ui-version=\"2.13.0\"] .popover-positioner .popover-top-bottom-padding{padding:1rem 0;width:100%}[data-gene-ui-version=\"2.13.0\"] .popover-positioner .popover-top-bottom-padding>div[style*=width]{width:100%!important}[data-gene-ui-version=\"2.13.0\"] .popover{width:100%}[data-gene-ui-version=\"2.13.0\"] .popover-positioner.mobile-view .popover{height:100%;position:relative}[data-gene-ui-version=\"2.13.0\"] .popover-content{background:var(--background);border-radius:var(--popover-border-radius);box-shadow:var(--popover-shadow);overflow:hidden;padding:0 0 env(safe-area-inset-bottom);transition:padding .4s;width:100%}[data-gene-ui-version=\"2.13.0\"] .popover-positioner.mobile-view .popover-content{-webkit-overflow-scrolling:auto;bottom:0;left:0;max-height:calc(100% - 10rem);overflow-x:hidden;overflow-y:auto;position:absolute;z-index:1}[data-gene-ui-version=\"2.13.0\"] .popover-positioner.mobile-view[style*=\"opacity: 0\"] .popover-content{transform:translateY(100%)}[data-gene-ui-version=\"2.13.0\"] .popover-positioner.mobile-view .popover-content{transform:translateY(0)}[data-gene-ui-version=\"2.13.0\"] .fullHeight{height:100%}[data-gene-ui-version=\"2.13.0\"] .popover-mobile-backdrop{background:#0003;height:100%;left:0;opacity:1;position:absolute;top:0;width:100%;z-index:0}[data-gene-ui-version=\"2.13.0\"] .popover-footer,[data-gene-ui-version=\"2.13.0\"] .popover-header{background:var(--background);position:-webkit-sticky;position:sticky;z-index:1}[data-gene-ui-version=\"2.13.0\"] .popover-header{border-bottom:1px solid rgba(var(--background-sc-rgb),.1);top:0}[data-gene-ui-version=\"2.13.0\"] .popover-footer{align-items:stretch;border-top:1px solid rgba(var(--background-sc-rgb),.1);bottom:calc(env(safe-area-inset-bottom)*-1);display:flex;padding:0 0 env(safe-area-inset-bottom);transition:padding .4s}[data-gene-ui-version=\"2.13.0\"] .popover-body{position:relative;z-index:0}[data-gene-ui-version=\"2.13.0\"] .popover-search{padding:1.4rem 1.5rem}[data-gene-ui-version=\"2.13.0\"] .empty-data-holder{align-items:center;display:flex;height:19rem;justify-content:center;width:100%}[data-gene-ui-version=\"2.13.0\"] .popover-disabled{opacity:.5;pointer-events:none;transition:opacity .5s}";
styleInject(css_248z);

const setRef = (ref, currentRef) => {
  if (!ref) return;
  if (typeof ref === 'function') {
    ref(currentRef);
  } else {
    ref.current = currentRef;
  }
};
const Popover = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    extendTargetWidth,
    disableReposition,
    toggleHandler,
    cornerRadius,
    screenType,
    minHeight,
    maxHeight,
    className,
    children,
    position,
    disabled,
    Content,
    padding,
    behave,
    Header,
    Footer,
    align,
    scrollbarNeeded,
    contentRef,
    isOpen,
    getScrollRef,
    swipeable,
    onSwipedDown,
    scrollbarProps,
    fullHeight,
    ...restProps
  } = props;
  const id = guid();
  const popoverBodyRef = useRef(null);
  const [popoverOpened, setPopoverOpened] = useState(false);
  const isControlled = ('isOpen' in props);
  const popoverState = isControlled ? isOpen : popoverOpened;
  const {
    isMobile
  } = useDeviceType(screenType);
  const isToggle = behave === 'toggle';
  const [targetWidth, setTargetWidth] = useState(null);
  const [swipingPosition, setSwipingPosition] = useState(0);
  const [portalContainerRef, updatePortalContainerRef] = useUpdatableRef(null);
  const {
    geneUIProviderRef
  } = useContext(GeneUIDesignSystemContext);
  const checkBodyContains = event => popoverBodyRef.current.contains(event.target);
  const handleSwiped = event => setSwipingPosition(0);
  const handleSwipedDown = touchEvent => {
    !checkBodyContains(touchEvent.event) && onSwipedDown(touchEvent);
  };
  const handleSwiping = useCallback(_ref => {
    let {
      deltaY,
      event
    } = _ref;
    if (checkBodyContains(event)) {
      stopEvent(event);
    } else if (deltaY >= 0) {
      setSwipingPosition(deltaY * -1);
    }
  }, [popoverBodyRef, setSwipingPosition]);
  const swipeHandlers = useSwipeable(useMemo(() => swipeable ? {
    onSwipedDown: handleSwipedDown,
    onSwiping: handleSwiping,
    onSwiped: handleSwiped
  } : {}, [swipeable, handleSwipedDown, handleSwiping, handleSwiped]));
  const popoverStateChange = useCallback(event => {
    stopEvent(event);
    if (isControlled) {
      toggleHandler(event, isOpen);
    } else {
      setPopoverOpened(isPopoverOpened => {
        toggleHandler(event, !isPopoverOpened);
        return !isPopoverOpened;
      });
    }
  }, [isControlled, toggleHandler, isOpen]);
  const handlePopoverStateChange = useCallback(event => {
    Children.map(children, child => child.props.onClick && child.props.onClick(event));
    popoverStateChange(event);
  }, [children, popoverStateChange]);
  const setPopoverRef = popoverRef => {
    setRef(ref, popoverRef);
    const rect = popoverRef && popoverRef.target.getBoundingClientRect();
    setTargetWidth(rect && rect.width);
  };
  const handleClick = useCallback(e => {
    if (!disabled) {
      popoverState ? isToggle && handlePopoverStateChange(e) : handlePopoverStateChange(e);
    }
  }, [popoverState, isToggle, disabled, handlePopoverStateChange]);
  const generateContentRef = useCallback(element => {
    isMobile && getScrollRef(element);
    if (contentRef) {
      typeof contentRef === 'function' ? contentRef(element) : contentRef.current = element;
    }
  }, [isMobile, contentRef, getScrollRef]);
  const portalContent = /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("ul", {
    onClick: stopEvent,
    ref: generateContentRef,
    className: classnames('popover-content', {
      fullHeight: fullHeight && isMobile
    }),
    style: {
      bottom: swipingPosition
    }
  }, Header && /*#__PURE__*/React__default.createElement("li", {
    className: "popover-header"
  }, Header), /*#__PURE__*/React__default.createElement("li", {
    ref: popoverBodyRef,
    className: "popover-body"
  }, scrollbarNeeded && !isMobile ? /*#__PURE__*/React__default.createElement(CustomScrollbar, _extends({
    ref: scrollbar => {
      scrollbar && getScrollRef(scrollbar.view);
    },
    autoHeight: true,
    autoHeightMax: maxHeight,
    autoHeightMin: minHeight
  }, scrollbarProps), Content) : Content), Footer && /*#__PURE__*/React__default.createElement("li", {
    className: "popover-footer"
  }, Footer)), isMobile && /*#__PURE__*/React__default.createElement("div", {
    className: "popover-mobile-backdrop",
    onClick: popoverStateChange
  }));
  const getChild = useCallback((child, index) => /*#__PURE__*/React__default.cloneElement(child, {
    key: index,
    onClick: e => handleClick(e),
    className: classnames(child.props.className, 'cursor-pointer', {
      'popover-opened': popoverState,
      'popover-disabled': disabled
    })
  }), [handleClick, popoverState, disabled]);
  const childElements = useMemo(() => Children.map(children, getChild), [getChild, children]);
  useEffect(() => {
    !popoverState && updatePortalContainerRef(null);
  }, [popoverState]);
  return Content ? /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(_default, _extends({
    contentDestination: geneUIProviderRef.current,
    disableReposition: disableReposition,
    ref: setPopoverRef,
    isOpen: popoverState,
    position: position,
    align: align,
    onClickOutside: handlePopoverStateChange,
    containerClassName: classnames("popover-positioner", "cr-".concat(cornerRadius), className, {
      'mobile-view': isMobile
    }),
    transitionDuration: -1,
    padding: padding,
    content: /*#__PURE__*/React__default.createElement("div", swipeHandlers, /*#__PURE__*/React__default.createElement("div", {
      ref: updatePortalContainerRef,
      id: id,
      style: {
        width: extendTargetWidth && !isMobile && targetWidth || null
      }
    })),
    containerStyle: {
      overflow: null
    }
  }, restProps), childElements), portalContainerRef.current && /*#__PURE__*/React__default.createElement(Portal, {
    isOpen: true,
    container: portalContainerRef.current
  }, portalContent)) : childElements;
});
Popover.propTypes = {
  /**
   * The component that need to be displayed in the Popover. Any valid React node
   */
  Content: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * The component that need to be displayed as Popover header. Any valid React node
   */
  Header: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * The component that need to be displayed as Popover footer. Any valid React node
   */
  Footer: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * The component which click needs to trigger the Popover to open. Need to passed as child to Popover. Any valid React node
   */
  children: PropTypes.node,
  /**
   * The Popover will get the width of its child. The property will not work when "width" prop is specified
   */
  extendTargetWidth: PropTypes.bool,
  /**
   * The Popover "Content" minimum scroll height
   */
  minHeight: PropTypes.number,
  /**
   * The Popover "Content" maximum scroll height. Will not work when the "screenType" is "mobile"
   */
  maxHeight: PropTypes.number,
  /**
   * Use this prop to control the Popover. Note the component will start not to open and close automatically
   */
  isOpen: PropTypes.bool,
  /**
   * Popover position to be displayed
   */
  position: PropTypes.oneOfType([PropTypes.oneOf(popoverConfig.position), PropTypes.arrayOf(PropTypes.oneOf(popoverConfig.position))]),
  /**
   * Will called each time the popover need to be toggled(child click, close button click, backdrop click).
   * (event: Event, isOpen: bool) => void
   */
  toggleHandler: PropTypes.func,
  /**
   * Possible values are start, center, and end.
   * If start is specified, the popover content's top or left location is aligned with its target's.
   * With end specified, the content's bottom or right location is aligned with its target's.
   * If center is specified, the popover content and target's centers are aligned.
   */
  align: PropTypes.oneOf(popoverConfig.align),
  /**
   * If you'd like to apply styles to the single container div that your popover content is rendered within via stylesheets,
   * you can specify a custom className for the container here.
   */
  className: PropTypes.string,
  /**
   * This number determines the gap, in pixels, between your target content and your popover content
   */
  padding: PropTypes.number,
  /**
   * If this property is enabled, rather than the popover content repositioning on a boundary collision,
   * the popover content container will move beyond the window's bounds.
   * You are, however, supplied with nudgedLeft and nudgedTop values, so you may choose to handle content overflow as you wish.
   */
  disableReposition: PropTypes.bool,
  /**
   * Popover corner radius
   */
  cornerRadius: PropTypes.oneOf(popoverConfig.cornerRadius),
  /**
   * The switch between mobile and desktop version of Popover will be applied automatically, when the prop is not specified.
   * When the prop is present it must be changed from outside.
   */
  screenType: PropTypes.oneOf(screenTypes),
  /**
   * Specify does Popover needs to be toggled on child click
   */
  behave: PropTypes.oneOf(popoverConfig.behave),
  /**
   * Given content prop can have its own scroll, and with this props we can use/not use default scroll that has popover
   */
  scrollbarNeeded: PropTypes.bool,
  /**
   * Popover content ref
   */
  contentRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  disabled: PropTypes.bool,
  getScrollRef: PropTypes.func,
  /*
   * After DOWN swipe  (SwipeEventData) => void
   */
  onSwipedDown: PropTypes.func,
  /*
   * props for scrollbar
   */
  scrollbarProps: PropTypes.shape({
    ...CustomScrollbar.propTypes
  }),
  /*
   * Popup content opens with full height. Mobile only property.
   */
  fullHeight: PropTypes.bool,
  swipeable: PropTypes.bool
};
Popover.defaultProps = {
  cornerRadius: popoverConfig.cornerRadius[0],
  position: popoverConfig.position,
  align: popoverConfig.align[0],
  toggleHandler: noop,
  behave: popoverConfig.behave[0],
  fullHeight: false,
  extendTargetWidth: true,
  disableReposition: true,
  minHeight: 0,
  maxHeight: 510,
  padding: 10,
  getScrollRef: noop,
  scrollbarNeeded: true,
  swipeable: false,
  onSwipedDown: noop
};

export { Popover as default };
